import { ReactNode } from "react";
import { AitHeaderCell } from "./AitHeaderCell";
import { AitHeaderRowFirstCell } from "./AitHeaderRowFirstCell";
import { AitHeaderRowLastCell } from "./AitHeaderRowLastCell";
import { useTableContext } from "./TableContextProvider";
import { getBaseId } from "./functions/getBaseId";
import {
  AitCellType,
  AitColumnRepeat,
  AitHeaderCellData,
  AitHeaderRowData,
  AitLocationHeaderRow,
  AitTablePosition,
} from "./functions/interface";
import { trGetPositionFromLocation } from "./functions/trGetPositionFromLocation";

interface AitHeaderRowProps {
  location: AitLocationHeaderRow;
}

/**
 * Render a single header row with its header cells.
 *
 * @template T - Header payload type
 * @param props - Component props
 * @param props.location - The header row location
 */
export const AitHeaderRow = <T,>({ location }: AitHeaderRowProps): ReactNode => {
  const { state } = useTableContext<T>();
  const id = getBaseId(location, state?.id);
  const thisHeaderRow: AitHeaderRowData<T> | undefined = trGetPositionFromLocation(
    state,
    location,
    true,
  ).data;
  const columnRepeats = state?.processedTableData?.columnRepeats;

  return !state ||
    !state.processedTableData ||
    !thisHeaderRow ||
    !columnRepeats ||
    location.row === undefined ? null : (
    <tr
      id={id}
      role="row"
    >
      <AitHeaderRowFirstCell location={location} />
      {thisHeaderRow.cells.map((cell: AitHeaderCellData<T>, ci: number): React.ReactElement => {
        // Get cell from column repeat
        const cr: AitColumnRepeat | undefined =
          Array.isArray(columnRepeats) && columnRepeats.length > ci
            ? columnRepeats[ci]
            : // istanbul ignore next
              undefined;

        const isColumnRepeat =
          cr !== undefined && cr.colRepeat !== undefined
            ? cr.colRepeat.match(/^[[\]0,]+$/) === null
            : false;

        return (
          <AitHeaderCell
            location={{
              ...location,
              position: AitTablePosition.headerCell,
              cellId: cell.aitid,
              cellType: AitCellType.header,
              column: ci,
              colRepeat: cr?.colRepeat ?? "",
              isRepeat: location.isRepeat || isColumnRepeat,
              originalColumn: columnRepeats[ci].columnIndex,
            }}
            key={`${cell.aitid}-${ci}`}
          />
        );
      })}
      <AitHeaderRowLastCell location={location} />
    </tr>
  );
};

AitHeaderRow.displayName = "AitHeaderRow";
